/**
 * AI Suggestions Panel
 *
 * Displays AI-suggested documents for a checklist item with Accept/Reject actions.
 * Suggestions are filtered locally: rejected items are tracked in component state,
 * accepted items disappear via Convex reactivity when they appear in linkedDocumentIds.
 */

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, FileText, Check, X, AlertCircle } from "lucide-react";
import { Button, Badge } from "@/components/ui";
import { useEvidenceDocuments } from "@/hooks/use-evidence-documents";
import type { Id, SuggestedDocument, DocumentId } from "@/packages/types";

// ---- Confidence badge color mapping ----

const CONFIDENCE_COLORS: Record<
  "high" | "medium" | "low",
  { bg: string; text: string }
> = {
  high: { bg: "rgba(34,197,94,0.15)", text: "#22c55e" },
  medium: { bg: "rgba(234,179,8,0.15)", text: "#ca8a04" },
  low: { bg: "rgba(161,161,166,0.15)", text: "#a1a1a6" },
};

// ---- Suggestion Card ----

interface SuggestionCardProps {
  suggestion: SuggestedDocument;
  fileName: string | undefined;
  onAccept: () => void;
  onReject: () => void;
  index: number;
}

function SuggestionCard({
  suggestion,
  fileName,
  onAccept,
  onReject,
  index,
}: SuggestionCardProps) {
  const colors = CONFIDENCE_COLORS[suggestion.confidence];

  return (
    <motion.div
      className="rounded-lg border border-accent/20 bg-accent-soft/30 p-3 space-y-2"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0, padding: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
      layout
    >
      {/* Header: file name + confidence badge */}
      <div className="flex items-center gap-2">
        <FileText size={14} className="text-accent shrink-0" />
        <span className="text-[13px] font-medium text-primary truncate flex-1">
          {fileName ?? "Loading..."}
        </span>
        <Badge color={colors} className="capitalize text-[10px] shrink-0">
          {suggestion.confidence}
        </Badge>
        <span className="text-[10px] text-muted shrink-0">
          {Math.round(suggestion.score * 100)}%
        </span>
      </div>

      {/* Excerpt */}
      <p className="text-[12px] text-secondary leading-relaxed line-clamp-2">
        {suggestion.reason || suggestion.excerpt}
      </p>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReject}
          className="gap-1 text-muted hover:text-red-500 h-7 px-2"
        >
          <X size={13} />
          Reject
        </Button>
        <Button
          variant="accent-soft"
          size="sm"
          onClick={onAccept}
          className="gap-1 h-7 px-3"
        >
          <Check size={13} />
          Accept
        </Button>
      </div>
    </motion.div>
  );
}

// ---- Main Panel ----

interface AISuggestionsPanelProps {
  checklistItemId: Id<"checklistItems">;
  suggestions: SuggestedDocument[];
  linkedDocumentIds: DocumentId[];
  onAccept: (documentId: Id<"documents">) => void;
}

export function AISuggestionsPanel({
  suggestions,
  linkedDocumentIds,
  onAccept,
}: AISuggestionsPanelProps) {
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set());

  // Resolve document names for all suggestions via the same query used for evidence docs
  const suggestionDocIds = useMemo(
    () => suggestions.map((s) => s.documentId),
    [suggestions],
  );
  const { documents: suggestionDocs } = useEvidenceDocuments(suggestionDocIds);

  // Build a lookup map for document names
  const docNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const doc of suggestionDocs) {
      map.set(String(doc.id), doc.fileName);
    }
    return map;
  }, [suggestionDocs]);

  // Build set of linked document IDs for filtering accepted suggestions
  const linkedSet = useMemo(
    () => new Set(linkedDocumentIds),
    [linkedDocumentIds],
  );

  // Filter out rejected and already-linked (accepted) suggestions
  const visibleSuggestions = useMemo(
    () =>
      suggestions.filter(
        (s) => !rejectedIds.has(s.documentId) && !linkedSet.has(s.documentId),
      ),
    [suggestions, rejectedIds, linkedSet],
  );

  const handleReject = (documentId: DocumentId) => {
    setRejectedIds((prev) => {
      const next = new Set(prev);
      next.add(documentId);
      return next;
    });
  };

  // If all suggestions have been acted on, hide the panel entirely
  if (visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Section header */}
      <div className="flex items-center gap-1.5">
        <Sparkles size={13} className="text-accent" />
        <span className="text-[11px] font-semibold text-secondary uppercase tracking-wider">
          AI Suggestions ({visibleSuggestions.length})
        </span>
      </div>

      {/* Suggestion cards */}
      <AnimatePresence mode="popLayout">
        {visibleSuggestions.map((suggestion, i) => (
          <SuggestionCard
            key={suggestion.documentId}
            suggestion={suggestion}
            fileName={docNameMap.get(suggestion.documentId)}
            onAccept={() => onAccept(suggestion.documentId as Id<"documents">)}
            onReject={() => handleReject(suggestion.documentId)}
            index={i}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// ---- No Matches Message ----

export function AINoMatchesMessage() {
  return (
    <div className="flex flex-col items-center text-center py-4 gap-2">
      <AlertCircle size={24} className="text-muted/40" />
      <p className="text-[13px] font-medium text-secondary">
        No matching documents found
      </p>
      <p className="text-[12px] text-muted">
        Try uploading or linking an existing document instead.
      </p>
    </div>
  );
}
